// src/components/VisitorForm.tsx

import React, { useState, useEffect, FormEvent, ChangeEvent } from 'react';
import axios, { AxiosError } from 'axios';

//const API_URL = 'http://172.16.20.112:3000/api/visitors'; // 로컬 서버 주소
const API_URL = 'https://rek-hybrid.co.kr/api/visitors'; // 실제 서버 주소
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// 1. 폼 데이터 타입 정의
interface VisitorFormData {
  name: string;
  company: string;
  grade: string;
  mobile: string;
  mail: string;
}

// 2. Props 타입 정의
interface VisitorFormProps {
    numToEdit: number | null;
    onComplete: () => void;
}

function VisitorForm({ numToEdit, onComplete }: VisitorFormProps): React.JSX.Element { // 반환 타입 명시
  const [formData, setFormData] = useState<VisitorFormData>({
    name: '',
    company: '',
    grade: '',
    mobile: '',
    mail: '',
  });
  const [isEditMode, setIsEditMode] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [serverError, setServerError] = useState('');

  // 수정 모드일 때 기존 데이터 불러오기
  useEffect(() => {
    if (numToEdit) {
      setIsEditMode(true);
      axios.get<VisitorFormData>(`${API_URL}/${numToEdit}`) // 응답 데이터 타입 지정
        .then(response => {
          setFormData(response.data);
        })
        .catch(error => {
          console.error("데이터 로딩 실패:", error);
          setServerError("수정할 데이터를 불러오는 데 실패했습니다.");
        });
    } else {
      setIsEditMode(false);
      setFormData({ name: '', company: '', grade: '', mobile: '', mail: '' });
    }
  }, [numToEdit]);

  // 입력 값 변경 핸들러 (이벤트 타입: ChangeEvent<HTMLInputElement>)
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    if (name === 'mail') {
      if (value && !emailRegex.test(value)) {
        setEmailError('유효하지 않은 이메일 형식입니다.');
      } else {
        setEmailError('');
      }
    }
    setServerError('');
  };

  // 폼 제출 핸들러 (이벤트 타입: FormEvent<HTMLFormElement>)
  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!formData.name || !formData.company || !formData.mail || emailError) {
        alert("모든 필수 항목을 올바르게 입력해주세요.");
        return;
    }

    try {
      if (isEditMode) {
        await axios.patch(`${API_URL}/${numToEdit}`, formData);
        alert('방문자 정보가 성공적으로 수정되었습니다.');
      } else {
        await axios.post(API_URL, formData);
        alert('방문자가 성공적으로 등록되었습니다.');
      }
      onComplete();
    } catch (error: unknown) { // error 타입은 unknown
      const axiosError = error as AxiosError;
      
      console.error("API 요청 실패:", axiosError.response?.data || error);
      
      // NestJS 유효성 검사 오류 메시지 표시
      //const errorMessage = axiosError.response?.data?.message || '처리 중 오류가 발생했습니다.';
      //setServerError(Array.isArray(errorMessage) ? errorMessage.join(', ') : errorMessage);
    }
  };

  return (
    // Card 컴포넌트로 감싸서 깔끔한 컨테이너 제공
    <div className="card shadow-sm mb-5">
      <div className="card-header bg-primary text-white">
        <h4 className="mb-0">{isEditMode ? '방문자 정보 수정' : '방문자 등록'}</h4>
        <br></br>
        <p>등록된 방문자 정보는 홍보물 전송 활용 목적에만 사용됩니다.</p>
        <p>개인 정보 활용에 동의하는 경우에만 등록/수정해 주시기 바랍니다.</p>
      </div>
      <div className="card-body">
        <form onSubmit={handleSubmit}>
          
          {/* 이름 입력 필드 */}
          <div className="mb-3">
            <label htmlFor="name" className="form-label">이름*</label>
            <input 
                type="text" 
                className="form-control" 
                id="name" 
                name="name" 
                value={formData.name} 
                onChange={handleChange} 
                required 
            />
          </div>

          {/* 회사명 입력 필드 */}
          <div className="mb-3">
            <label htmlFor="company" className="form-label">회사명*</label>
            <input 
                type="text" 
                className="form-control" 
                id="company" 
                name="company" 
                value={formData.company} 
                onChange={handleChange} 
                required 
            />
          </div>

          {/* 직급 및 연락처 (반응형 레이아웃 적용) */}
          <div className="row">
            <div className="col-md-6 mb-3">
              <label htmlFor="grade" className="form-label">직급</label>
              <input type="text" className="form-control" id="grade" name="grade" value={formData.grade} onChange={handleChange} />
            </div>
            <div className="col-md-6 mb-3">
              <label htmlFor="mobile" className="form-label">연락처</label>
              <input type="text" className="form-control" id="mobile" name="mobile" value={formData.mobile} onChange={handleChange} />
            </div>
          </div>
          
          {/* 이메일 입력 필드 (유효성 검사 피드백 적용) */}
          <div className="mb-4">
            <label htmlFor="mail" className="form-label">이메일*</label>
            <input 
                type="email" 
                className={`form-control ${emailError ? 'is-invalid' : ''}`} // 에러 시 빨간색 경계선 표시
                id="mail" 
                name="mail" 
                value={formData.mail} 
                onChange={handleChange} 
                required 
            />
            {emailError && <div className="invalid-feedback">{emailError}</div>} {/* 에러 메시지 표시 */}
          </div>
          
          {serverError && <div className="alert alert-danger" role="alert">{serverError}</div>}
          
          {/* 버튼 디자인 개선 */}
          <button type="submit" className={`btn btn-${isEditMode ? 'success' : 'primary'} w-100 mt-2`}>
            {isEditMode ? '수정 완료' : '등록하기'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default VisitorForm;